import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useContext,
} from "react";
import { Modal } from "react-bootstrap";
import { Workbook } from "@fortune-sheet/react";
import "@fortune-sheet/react/dist/index.css";
import { Alert, Select } from "antd";
import { toast } from "react-toastify";
import Popup from "../../Helpers/Popup/Popup";
import Button from "../../Helpers/Button/Button";
import trans from "../../../utils";
import {
  useBulkCreateTasksMutation,
  useBulkCreateTasksFromExcelMutation,
} from "../../../Redux/service/Tasks/Tasks";
import { useTechnicians } from "../../../hooks/useTechnicians";
import { usePois } from "../../../hooks/usePOIs";
import { useLocalForms } from "../../../hooks/useForms";
import { popupcontext } from "../../../context/Popupscontext";

// ─── Column definitions ───────────────────────────────────────────────────────

const COL = {
  NAME: 0,
  TYPE: 1,
  PRIORITY: 2,
  START_TIME: 3,
  DURATION: 4,
  ASSIGNED_TO: 5,
  POI: 6,
  FORMS: 7,
  IS_FLEXIBLE: 8,
  NOTES: 9,
};

const COLUMN_HEADERS = [
  "Name",
  "Type",
  "Priority",
  "Start Time (YYYY-MM-DD HH:mm)",
  "Expected Duration (min)",
  "Assigned To",
  "POI",
  "Forms (comma separated)",
  "Is Flexible (true/false)",
  "Notes",
];

const COLUMN_WIDTHS = {
  [COL.NAME]: 220,
  [COL.TYPE]: 200,
  [COL.PRIORITY]: 160,
  [COL.START_TIME]: 230,
  [COL.DURATION]: 200,
  [COL.ASSIGNED_TO]: 200,
  [COL.POI]: 200,
  [COL.FORMS]: 250,
  [COL.IS_FLEXIBLE]: 180,
  [COL.NOTES]: 260,
};

const DROPDOWN_COLS = new Set([
  COL.TYPE,
  COL.PRIORITY,
  COL.ASSIGNED_TO,
  COL.POI,
  COL.FORMS,
  COL.IS_FLEXIBLE,
]);

const TYPE_OPTIONS = [
  "inspection",
  "delivery",
  "survey",
  "maintenance",
  "other",
];
const PRIORITY_OPTIONS = ["low", "medium", "high"];

// Fortune-sheet layout constants (showToolbar=true, showFormulaBar=false).
// Adjust if fortune-sheet renders at different sizes in your environment.
const FS_TOOLBAR_H = 41; // toolbar height
const FS_COL_HEADER_H = 20; // column-letter header row height
const FS_ROW_HEADER_W = 46; // row-number column width
const FS_ROW_H = 19; // default data row height

// ─── Sheet helpers ────────────────────────────────────────────────────────────

const buildSheet = (celldata) => ({
  name: "Tasks",
  celldata,
  row: 200,
  column: COLUMN_HEADERS.length,
  config: { columnlen: COLUMN_WIDTHS },
});

const buildSheetFrom2DData = (json2d) => ({
  name: "Tasks",
  data: json2d.map((row) =>
    row.map((cell) => {
      const val = cell?.toString() ?? "";
      return {
        v: val,
        m: val,
        ct: { fa: "General", t: "g" },
      };
    }),
  ),
  row: Math.max(200, json2d.length + 20),
  column: COLUMN_HEADERS.length,
  config: { columnlen: COLUMN_WIDTHS },
});

const buildHeaderCelldata = () =>
  COLUMN_HEADERS.map((label, c) => ({
    r: 0,
    c,
    v: {
      v: label,
      m: label,
      bl: 1,
      bg: "#237544",
      fc: "#FFFFFF",
      ct: { fa: "General", t: "g" },
    },
  }));

// ─── Row extraction ───────────────────────────────────────────────────────────

const getCellText = (cell) => {
  if (!cell) return "";
  return (cell.m ?? cell.v ?? "").toString().trim();
};

const extractRows = (sheetData) => {
  if (!sheetData?.length) return [];
  const sheet = sheetData[0];

  // Dense format — returned by onChange after any user interaction
  if (Array.isArray(sheet.data) && sheet.data.length > 0) {
    return sheet.data.slice(1).map((rowArr) => ({
      name: getCellText(rowArr?.[COL.NAME]),
      type: getCellText(rowArr?.[COL.TYPE]),
      priority: getCellText(rowArr?.[COL.PRIORITY]),
      startTime: getCellText(rowArr?.[COL.START_TIME]),
      expectedDuration: getCellText(rowArr?.[COL.DURATION]),
      assignedTo: getCellText(rowArr?.[COL.ASSIGNED_TO]),
      poi: getCellText(rowArr?.[COL.POI]),
      forms: getCellText(rowArr?.[COL.FORMS]),
      isFlexible: getCellText(rowArr?.[COL.IS_FLEXIBLE]),
      notes: getCellText(rowArr?.[COL.NOTES]),
    }));
  }

  // Sparse format — only present before any user interaction
  const celldata = sheet.celldata || [];
  if (celldata.length === 0) return [];
  const maxRow = celldata.reduce((max, cell) => Math.max(max, cell.r), 0);
  return Array.from({ length: maxRow }, (_, i) => i + 1).map((r) => {
    const cells = {};
    celldata
      .filter((cell) => cell.r === r)
      .forEach((cell) => {
        cells[cell.c] = getCellText(cell.v);
      });
    return {
      name: cells[COL.NAME] ?? "",
      type: cells[COL.TYPE] ?? "",
      priority: cells[COL.PRIORITY] ?? "",
      startTime: cells[COL.START_TIME] ?? "",
      expectedDuration: cells[COL.DURATION] ?? "",
      assignedTo: cells[COL.ASSIGNED_TO] ?? "",
      poi: cells[COL.POI] ?? "",
      notes: cells[COL.NOTES] ?? "",
    };
  });
};

// ─── Validation ───────────────────────────────────────────────────────────────

const isRowEmpty = (row) =>
  !row.name &&
  !row.type &&
  !row.priority &&
  !row.startTime &&
  !row.expectedDuration;

// Returns array of {r, c} for cells that fail required-field rules.
// Skips fully empty rows.
const getInvalidCells = (allRows) => {
  const invalid = [];
  allRows.forEach((row, idx) => {
    if (isRowEmpty(row)) return;
    const r = idx + 1; // row 0 is the header
    if (!row.name) invalid.push({ r, c: COL.NAME });
    if (!row.type || !TYPE_OPTIONS.includes(row.type.toLowerCase()))
      invalid.push({ r, c: COL.TYPE });
    if (!row.priority || !PRIORITY_OPTIONS.includes(row.priority.toLowerCase()))
      invalid.push({ r, c: COL.PRIORITY });
    if (!row.startTime) invalid.push({ r, c: COL.START_TIME });
    if (!row.assignedTo) invalid.push({ r, c: COL.ASSIGNED_TO });
    if (
      !row.expectedDuration ||
      isNaN(Number(row.expectedDuration)) ||
      Number(row.expectedDuration) <= 0
    )
      invalid.push({ r, c: COL.DURATION });
  });
  return invalid;
};

// ─── Misc helpers ─────────────────────────────────────────────────────────────

// Cumulative left pixel offset for column c (excludes the row-number column)
const getColLeft = (col) => {
  let left = 0;
  for (let c = 0; c < col; c++) left += COLUMN_WIDTHS[c] ?? 100;
  return left;
};

// Parse "Label - ID" format stored in Assigned To / POI cells
const parseStoredId = (stored) => {
  const match = stored?.match(/ - (\d+)$/);
  return match ? Number(match[1]) : null;
};

const formatIsoToSheetDate = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d} ${hh}:${mm}`;
};

const BACKEND_FIELD_TO_COL = {
  name: COL.NAME,
  type: COL.TYPE,
  priority: COL.PRIORITY,
  startTime: COL.START_TIME,
  expectedDuration: COL.DURATION,
  employeeCode: COL.ASSIGNED_TO,
  assignedToId: COL.ASSIGNED_TO,
  poiCode: COL.POI,
  poiId: COL.POI,
  formCode: COL.FORMS,
  forms: COL.FORMS,
  isFlexible: COL.IS_FLEXIBLE,
  notes: COL.NOTES,
};

const safeParseJsonObject = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

const resolveFailedRow = (item) => {
  // Support multiple backend shapes
  return (
    safeParseJsonObject(item?.row) ||
    safeParseJsonObject(item?.failedRow) ||
    safeParseJsonObject(item?.data?.row) ||
    safeParseJsonObject(item?.payload?.row) ||
    safeParseJsonObject(item) ||
    {}
  );
};

const resolveFailedErrors = (item) => {
  const raw =
    item?.error ??
    item?.errors ??
    item?.validationErrors ??
    item?.data?.error ??
    item?.data?.errors ??
    [];
  if (Array.isArray(raw)) return raw;
  const parsed = safeParseJsonObject(raw);
  return Array.isArray(parsed) ? parsed : [];
};

const mapFailedRowsToSheet = (failed, technicians, pois, forms) => {
  const toLabelWithId = (options, id) => {
    if (!id) return "";
    const found = options.find((o) => Number(o.value) === Number(id));
    return found ? `${found.label} - ${id}` : String(id);
  };

  const toFormsLabelWithIds = (formIds) => {
    if (!Array.isArray(formIds) || formIds.length === 0) return "";
    return formIds
      .map((id) => {
        const found = forms.find((f) => Number(f.value) === Number(id));
        return found ? `${found.label} - ${id}` : String(id);
      })
      .join(", ");
  };
  const failedRows2d = [
    COLUMN_HEADERS,
    ...failed.map((item) => {
      const row = resolveFailedRow(item);

      const parseFormCodes = (value) => {
        if (!value) return [];

        // handle "273" OR "273,274"
        return value
          .toString()
          .split(",")
          .map((v) => Number(v.trim()))
          .filter(Boolean);
      };

      return [
        row?.name ?? "",
        row?.type ?? "",
        row?.priority ?? "",
        formatIsoToSheetDate(row?.startTime),
        row?.expectedDuration ?? "",
        toLabelWithId(technicians, row?.assignedToId || row?.employeeCode),
        toLabelWithId(pois, row?.poiId || row?.poiCode),
        toFormsLabelWithIds(
          row?.forms?.length ? row.forms : parseFormCodes(row?.formCode),
        ),
        String(row?.isFlexible ?? false),
        row?.notes ?? "",
      ];
    }),
  ];

  const backendInvalidCells = [];
  failed.forEach((item, idx) => {
    const rowIndexInSheet = idx + 1;
    resolveFailedErrors(item).forEach((err) => {
      const col = BACKEND_FIELD_TO_COL[err?.field];
      if (typeof col === "number") {
        backendInvalidCells.push({ r: rowIndexInSheet, c: col });
      }
    });
  });

  return { failedRows2d, backendInvalidCells };
};

// ─── Component ────────────────────────────────────────────────────────────────

const TaskBulkUpload = ({ show, onClose }) => {
  const { options: technicians } = useTechnicians();
  const { options: pois } = usePois();
  const { options: forms } = useLocalForms();

  const { setEdited } = useContext(popupcontext);

  const [sheetData, setSheetData] = useState(() => [
    buildSheet(buildHeaderCelldata()),
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [overlay, setOverlay] = useState(null); // searchable select overlay
  const [invalidCells, setInvalidCells] = useState([]); // [{r,c}] — drives the red highlight divs
  const [scrollOffset, setScrollOffset] = useState({ left: 0, top: 0 });
  const [pendingMatrix, setPendingMatrix] = useState(null);

  const workbookRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const overlayRef = useRef(null);

  const [bulkCreateTasks] = useBulkCreateTasksMutation();
  const [bulkCreateTasksFromExcel] = useBulkCreateTasksFromExcelMutation();

  const applyProgrammaticMatrix = useCallback((matrix2d) => {
    setSheetData([buildSheetFrom2DData(matrix2d)]);
    setPendingMatrix(matrix2d);
  }, []);

  useEffect(() => {
    if (!pendingMatrix || !workbookRef.current) return;
    const wb = workbookRef.current;
    pendingMatrix.forEach((row, r) => {
      row.forEach((cell, c) => {
        wb.setCellValue?.(r, c, cell?.toString?.() ?? "");
      });
    });
    setPendingMatrix(null);
  }, [pendingMatrix]);

  // ── Track fortune-sheet's internal scroll so highlights stay in sync ────────
  useEffect(() => {
    if (!show) return;
    const container = containerRef.current;
    if (!container) return;

    // capture=true intercepts scroll from any nested scrollable (fortune-sheet's inner grid)
    const handleScroll = (e) => {
      const el = e.target;
      setScrollOffset((prev) => ({
        left: el.scrollLeft !== undefined ? el.scrollLeft : prev.left,
        top: el.scrollTop !== undefined ? el.scrollTop : prev.top,
      }));
    };

    container.addEventListener("scroll", handleScroll, true);
    return () => container.removeEventListener("scroll", handleScroll, true);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    // Always start from a guaranteed header sheet when popup opens
    setSheetData([buildSheet(buildHeaderCelldata())]);
    setPendingMatrix(null);
    setInvalidCells([]);
    setUploadSummary(null);
    setOverlay(null);
    setScrollOffset({ left: 0, top: 0 });
  }, [show]);

  // ── Dropdown overlay options ─────────────────────────────────────────────────
  const getColumnOptions = useCallback(
    (col) => {
      switch (col) {
        case COL.TYPE:
          return TYPE_OPTIONS.map((v) => ({ label: v, value: v }));
        case COL.PRIORITY:
          return PRIORITY_OPTIONS.map((v) => ({ label: v, value: v }));
        case COL.ASSIGNED_TO:
          return technicians.map((t) => ({
            label: t.label,
            value: `${t.label} - ${t.value}`,
          }));
        case COL.POI:
          return pois.map((p) => ({
            label: p.label,
            value: `${p.label} - ${p.value}`,
          }));

        case COL.FORMS:
          return forms.map((f) => ({
            label: f.label,
            value: `${f.label} - ${f.value}`,
          }));

        case COL.IS_FLEXIBLE:
          return [
            { label: "true", value: "true" },
            { label: "false", value: "false" },
          ];
        default:
          return [];
      }
    },
    [technicians, pois, forms],
  );

  // ── Click → show searchable select overlay for dropdown columns ─────────────
  const handleContainerClick = useCallback((e) => {
    setTimeout(() => {
      const wb = workbookRef.current;
      if (!wb) return;
      const selections = wb.getSelection?.();
      if (!selections?.length) return;
      const {
        row: [row],
        column: [col],
      } = selections[0];
      if (row === 0 || !DROPDOWN_COLS.has(col)) {
        setOverlay(null);
        return;
      }
      const rawVal = wb.getCellValue?.(row, col) ?? "";
      const x = Math.min(
        e.clientX,
        window.innerWidth - COLUMN_WIDTHS[col] - 20,
      );
      const y = e.clientY + 4;
      setOverlay({ col, row, currentVal: String(rawVal ?? ""), x, y });
    }, 30);
  }, []);

  useEffect(() => {
    if (!overlay) return;
    const onMouseDown = (e) => {
      if (!overlayRef.current?.contains(e.target)) setOverlay(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [overlay]);

  const handleOverlayChange = (value) => {
    if (!overlay) return;

    const finalValue = overlay.col === COL.FORMS ? value.join(", ") : value;

    workbookRef.current?.setCellValue(overlay.row, overlay.col, finalValue);
    setOverlay(null);
  };

  // ── Sheet change ─────────────────────────────────────────────────────────────
  const handleSheetChange = useCallback((data) => {
    setSheetData(data);
  }, []);

  // ── Template download ────────────────────────────────────────────────────────
  const handleDownloadTemplate = () => {
    const basePath = process.env.PUBLIC_URL?.replace(/\/$/, "") || "";
    const templatePath = `${basePath}/templates/tasks_bulk_template.xlsx`;
    const link = document.createElement("a");
    link.href = templatePath;
    link.download = "tasks_bulk_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // ── Excel upload ─────────────────────────────────────────────────────────────
  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSheetData([buildSheet(buildHeaderCelldata())]);
    setPendingMatrix(null);
    setInvalidCells([]);
    setUploadSummary(null);
    setOverlay(null);
    setScrollOffset({ left: 0, top: 0 });

    await new Promise((res) => setTimeout(res, 0)); // 👈 ensure reset applied

    try {
      const converted = await bulkCreateTasksFromExcel(file).unwrap();

      // New API shape: backend creates valid tasks immediately and returns only failed rows
      const successful = converted?.data?.successful ?? [];
      const failed =
        converted?.data?.failed ??
        converted?.data?.failedRows ??
        converted?.failed ??
        converted?.failedRows ??
        [];
      const hasNewShape =
        converted?.status ||
        converted?.data?.successful ||
        converted?.data?.failed;

      if (hasNewShape) {
        const successCount = successful.length;
        const failedCount = failed.length;
        setUploadSummary({
          status:
            converted?.status ?? (failedCount ? "partial_success" : "success"),
          message:
            converted?.message ??
            `${successCount} ${trans("tasks created")}, ${failedCount} ${trans("failed")}`,
          successCount,
          failedCount,
        });

        if (failedCount === 0) {
          applyProgrammaticMatrix([COLUMN_HEADERS]);
          setInvalidCells([]);
          toast.success(
            converted?.message ??
              `${successCount} ${trans("task(s) created successfully")}`,
          );
          e.target.value = "";
          return;
        }

        if (failedCount > 199) {
          toast.error(
            `${failedCount} ${trans("failed rows — too many errors to display")}`,
            { autoClose: 5000 },
          );
          e.target.value = "";
          return;
        }

        const { failedRows2d, backendInvalidCells } = mapFailedRowsToSheet(
          failed,
          technicians,
          pois,
          forms,
        );

        applyProgrammaticMatrix(failedRows2d);
        setInvalidCells(backendInvalidCells);
        toast.info(
          converted?.message ??
            `${successCount} ${trans("task(s) created")}, ${failedCount} ${trans("failed row(s) loaded for correction")}`,
        );
        e.target.value = "";
        return;
      }

      // Backward compatibility: old converter returned rows only
      const rows = Array.isArray(converted) ? converted : converted?.data;
      if (!Array.isArray(rows) || rows.length === 0) {
        toast.error(trans("The uploaded file is empty"));
        e.target.value = "";
        return;
      }

      setUploadSummary(null);

      const toLabelWithId = (options, id) => {
        if (!id) return "";
        const found = options.find((o) => Number(o.value) === Number(id));
        return found ? `${found.label} - ${id}` : String(id);
      };

      const toFormsLabelWithIds = (formIds) => {
        if (!Array.isArray(formIds) || formIds.length === 0) return "";
        return formIds
          .map((id) => {
            const found = forms.find((f) => Number(f.value) === Number(id));
            return found ? `${found.label} - ${id}` : String(id);
          })
          .join(", ");
      };

      const preview2d = [
        COLUMN_HEADERS,
        ...rows.map((task) => [
          task?.name ?? "",
          task?.type ?? "",
          task?.priority ?? "",
          formatIsoToSheetDate(task?.startTime),
          task?.expectedDuration ?? "",
          toLabelWithId(technicians, task?.assignedToId),
          toLabelWithId(pois, task?.poiId),
          toFormsLabelWithIds(task?.forms),
          String(task?.isFlexible ?? false),
          task?.notes ?? "",
        ]),
      ];

      applyProgrammaticMatrix(preview2d);
      setInvalidCells([]);
      toast.success(
        trans("File converted — review the data below before submitting"),
      );
    } catch (err) {
      toast.error(
        err?.data?.message ??
          trans(
            "Excel conversion failed. Please check the file and try again.",
          ),
      );
    }

    e.target.value = "";
  };

  // ── Submit: validate → highlight → bulk POST ─────────────────────────────────
  const handleSubmit = async () => {
    const allRows = extractRows(sheetData);
    const nonEmptyRows = allRows.filter((row) => !isRowEmpty(row));

    if (nonEmptyRows.length === 0) {
      toast.error(trans("No data to submit. Please fill in at least one row."));
      return;
    }

    // 1. Validate — paint red overlay on invalid cells
    const invalid = getInvalidCells(allRows);
    setInvalidCells(invalid);

    if (invalid.length > 0) {
      toast.error(
        `${invalid.length} ${trans("required cell(s) are missing or invalid — highlighted in red")}`,
        { autoClose: 4000 },
      );
      return;
    }

    // 2. All valid — clear overlays and build payload
    setInvalidCells([]);

    const unresolvedRefCells = [];
    const tasks = nonEmptyRows.map((row, idx) => {
      const rowIndexInSheet = allRows.findIndex((r) => r === row) + 1;
      const assignedToId =
        parseStoredId(row.assignedTo) ??
        (technicians.find(
          (t) => t.label.toLowerCase() === row.assignedTo.toLowerCase(),
        )?.value
          ? Number(
              technicians.find(
                (t) => t.label.toLowerCase() === row.assignedTo.toLowerCase(),
              ).value,
            )
          : undefined);
      const poiId =
        parseStoredId(row.poi) ??
        (pois.find((p) => p.label.toLowerCase() === row.poi.toLowerCase())
          ?.value
          ? Number(
              pois.find((p) => p.label.toLowerCase() === row.poi.toLowerCase())
                .value,
            )
          : undefined);

      const formIds = parseMultiIds(row.forms);
      const hasFormsInput = Boolean(row.forms?.trim());
      if (row.assignedTo && !assignedToId) {
        unresolvedRefCells.push({ r: rowIndexInSheet, c: COL.ASSIGNED_TO });
      }
      if (row.poi && !poiId) {
        unresolvedRefCells.push({ r: rowIndexInSheet, c: COL.POI });
      }
      if (hasFormsInput && formIds.length === 0) {
        unresolvedRefCells.push({ r: rowIndexInSheet, c: COL.FORMS });
      }

      const isFlexible = row.isFlexible?.toLowerCase() === "true";
      const task = {
        name: row.name,
        type: row.type.toLowerCase(),
        priority: row.priority.toLowerCase(),
        startTime: new Date(row.startTime).toISOString(),
        expectedDuration: Number(row.expectedDuration),
      };
      if (assignedToId) task.assignedToId = Number(assignedToId);
      if (poiId) task.poiId = Number(poiId);
      if (formIds.length) task.forms = formIds;
      task.isFlexible = isFlexible;
      if (row.notes) task.notes = row.notes;
      return task;
    });

    if (unresolvedRefCells.length > 0) {
      setInvalidCells(unresolvedRefCells);
      toast.error(
        trans(
          "Some Assigned To, POI, or Forms values could not be resolved to valid IDs — highlighted in red",
        ),
        { autoClose: 5000 },
      );
      return;
    }

    // 3. Single bulk request
    setIsSubmitting(true);
    try {
      const response = await bulkCreateTasks(tasks).unwrap();
      const successful =
        response?.data?.successful ??
        response?.successful ??
        response?.data?.created ??
        response?.created ??
        [];
      const failed =
        response?.data?.failed ??
        response?.data?.failedRows ??
        response?.failed ??
        response?.failedRows ??
        [];
      const hasBulkSummaryShape =
        response?.status ||
        response?.data?.successful ||
        response?.data?.failed ||
        response?.successful ||
        response?.failed;

      if (hasBulkSummaryShape) {
        const successCount = Array.isArray(successful)
          ? successful.length
          : Number(response?.data?.successCount ?? response?.successCount ?? 0);
        const failedCount = Array.isArray(failed)
          ? failed.length
          : Number(response?.data?.failedCount ?? response?.failedCount ?? 0);
        const status =
          response?.status ?? (failedCount > 0 ? "partial_success" : "success");
        setUploadSummary({
          status,
          message:
            response?.message ??
            `${successCount} ${trans("tasks created")}, ${failedCount} ${trans("failed")}`,
          successCount,
          failedCount,
        });

        if (failedCount > 0 && Array.isArray(failed)) {
          toast.warning(
            response?.message ??
              `${successCount} ${trans("task(s) created")}, ${failedCount} ${trans("failed row(s) loaded for correction")}`,
            { autoClose: 5000, rtl: localStorage.getItem("lang") === "ar" },
          );
          return;
        }

        toast.success(
          response?.message ??
            `${successCount} ${trans("task(s) created successfully")}`,
          {
            autoClose: 1500,
            rtl: localStorage.getItem("lang") === "ar",
          },
        );
        handleClose();
        return;
      }

      toast.success(
        `${tasks.length} ${trans("task(s) created successfully")}`,
        {
          autoClose: 1500,
          rtl: localStorage.getItem("lang") === "ar",
        },
      );
      handleClose();
    } catch (err) {
      toast.error(
        err?.data?.message ??
          trans("Bulk upload failed. Please check the data and try again."),
        { autoClose: 5000 },
      );
    } finally {
      setIsSubmitting(false);
      setEdited(true);
    }
  };

  const handleClose = () => {
    // Use direct header reset instead of workbook hydration path
    setSheetData([buildSheet(buildHeaderCelldata())]);
    setPendingMatrix(null);
    setInvalidCells([]);
    setUploadSummary(null);
    setOverlay(null);
    setScrollOffset({ left: 0, top: 0 });
    onClose();
  };

  const parseMultiIds = (value) => {
    if (!value) return [];

    return value
      .split(",")
      .map((item) => {
        const match = item.trim().match(/ - (\d+)$/);
        return match ? Number(match[1]) : null;
      })
      .filter(Boolean);
  };

  return (
    <Popup show={show} size="xl">
      <Modal.Header className="modal-header flex-between">
        <Modal.Title>
          <div>
            <div className="fw-bold fs-5">{trans("Bulk Add Tasks")}</div>
            <div className="text-muted small">
              {trans(
                "Fill the spreadsheet, upload an Excel file, or download the template first",
              )}
            </div>
          </div>
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleClose}
        />
      </Modal.Header>

      <Modal.Body
        style={{
          padding: 0,
          height: "60vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {uploadSummary && (
          <div style={{ padding: "12px 16px 0 16px" }}>
            <Alert
              showIcon
              type={uploadSummary.failedCount > 0 ? "warning" : "success"}
              message={uploadSummary.message}
            />
          </div>
        )}
        <div
          ref={containerRef}
          style={{ flex: 1, overflow: "hidden", position: "relative" }}
          onClick={handleContainerClick}
        >
          <Workbook
            ref={workbookRef}
            data={sheetData}
            onChange={handleSheetChange}
            showFormulaBar={false}
            showSheetTabs={false}
            showToolbar={true}
          />

          {/* ── Invalid-cell highlight overlays ──────────────────────────────
              Absolutely positioned semi-transparent divs drawn on top of the
              sheet. pointer-events:none lets all clicks pass through.
              We avoid touching fortune-sheet's internal state entirely.       */}
          {invalidCells.map(({ r, c }) => {
            const left = FS_ROW_HEADER_W + getColLeft(c) - scrollOffset.left;
            const top =
              FS_TOOLBAR_H + FS_COL_HEADER_H + r * FS_ROW_H - scrollOffset.top;
            // Skip cells scrolled out of the visible grid area
            if (left + COLUMN_WIDTHS[c] <= FS_ROW_HEADER_W) return null;
            if (top < FS_TOOLBAR_H + FS_COL_HEADER_H) return null;
            return (
              <div
                key={`${r}_${c}`}
                style={{
                  position: "absolute",
                  left,
                  top,
                  width: COLUMN_WIDTHS[c],
                  height: FS_ROW_H,
                  backgroundColor: "rgba(220, 38, 38, 0.18)",
                  border: "1px solid rgba(220, 38, 38, 0.6)",
                  pointerEvents: "none",
                  zIndex: 100,
                  boxSizing: "border-box",
                }}
              />
            );
          })}
        </div>

        {/* Searchable dropdown overlay */}
        {overlay && (
          <div
            ref={overlayRef}
            style={{
              position: "fixed",
              left: overlay.x,
              top: overlay.y,
              zIndex: 9999,
              width: Math.max(COLUMN_WIDTHS[overlay.col], 220),
              boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              borderRadius: 6,
              background: "#fff",
            }}
          >
            <Select
              showSearch
              autoFocus
              open
              mode={overlay.col === COL.FORMS ? "multiple" : undefined}
              style={{ width: "100%" }}
              value={
                overlay.col === COL.FORMS
                  ? overlay.currentVal?.split(", ").filter(Boolean)
                  : overlay.currentVal || undefined
              }
              placeholder={trans("Search or select...")}
              options={getColumnOptions(overlay.col)}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleOverlayChange}
              getPopupContainer={() => overlayRef.current ?? document.body}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
        <div className="d-flex gap-2">
          <Button
            type="default"
            text={trans("Download Template")}
            onClick={handleDownloadTemplate}
          />
          <Button
            type="default"
            text={trans("Upload Excel")}
            onClick={handleUploadClick}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>
        <div className="d-flex gap-2">
          <Button type="default" text={trans("Cancel")} onClick={handleClose} />
          <Button
            type="primary"
            text={trans("Submit")}
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </Modal.Footer>
    </Popup>
  );
};

export default TaskBulkUpload;
